// src/components/OrdersItemsTable.tsx
import React, { useMemo, useState } from 'react';
import { Table, ConfigProvider, Button, Tag, Dropdown, InputNumber, message } from 'antd';
import { updateOrderStatus, setOrderDiscount } from '../services/orderSync';
import ruRU from 'antd/es/locale/ru_RU';
import type { ColumnsType } from 'antd/es/table';
import { OrdersTableOrder } from './OrdersItemsFromNotifications';
import { useResizableColumns, ResCol } from '../hooks/useResizableColumns';
import { formatDateTime } from '../utils/dates';
import { formatCurrency } from '../utils/numbers';

const CATEGORY_LABELS: Record<string, string> = {
  spark_plugs:                 'Свечи зажигания',
  ignition_coils:              'Катушки зажигания',
  crankshaft_sensors:          'Датчики',
  airbag_cables:               'Рулевые шлейфы',
  ignition_coil_mouthpieces:   'Наконечники',
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  original: 'Оригинал',
  copy:     'Копия',
};

type Row = {
  key: string;
  notifId: string;
  rowIds: number[];  // строки ordered_products этого заказа — статус меняется по ним
  phone?: string;
  orderNumber: string;
  date: string;
  dateTs: number;
  status: string;
  product: string;   // категория (Катушки, Свечи, …)
  quality: string;   // Оригинал/Копия
  sku: string;       // newline-separated part numbers
  qty: number;
  price: number | null;
  total: number | null;
  discount?: number;
  effectiveTotal?: number;
};

const orderNoWeight = (s: string) => {
  const m = String(s).match(/(\d+)(?!.*\d)/);
  return m ? parseInt(m[1], 10) : NaN;
};

const fmtOrderNo = (raw: unknown): string => {
  const s = String(raw ?? '').trim();
  if (!s) return '—';

  // если в строке есть длинная числовая последовательность — используем её
  const num = s.match(/\d{4,}/);
  if (num) return `ORD-${num[0]}`;

  // иначе компактный код из последних 10 alnum-символов
  const alnum = s.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const short = (alnum || '0000000000').slice(-10);
  return `ORD-${short}`;
};

type Props = {
  orders: OrdersTableOrder[];
  maxRows?: number;
  onMarkSold?: (id: string, phone?: string) => Promise<void>;
  onStatusChanged?: () => Promise<void>;
};

const OrdersItemsTable: React.FC<Props> = ({ orders, maxRows = 500, onMarkSold, onStatusChanged }) => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [overrideStatus, setOverrideStatus] = useState<Record<string, string>>({});
  const [savingDiscount, setSavingDiscount] = useState<Set<string>>(new Set());
  const [discountOverrides, setDiscountOverrides] = useState<Record<string, number>>({});
  // Набираемое число — до ухода из поля или Enter. Раньше сохранялось на каждую клавишу,
  // поле на время сохранения блокировалось, и из «10» доходила только «1» (31.10.2026).
  const [discountDrafts, setDiscountDrafts] = useState<Record<string, number | null>>({});

  const BACKEND_TO_NOTIF: Record<string, string> = {
    sold: 'shipped', canceled: 'canceled', return: 'returned',
  };

  // Статус — у одного заказа (по его строкам), а не у всех заказов телефона (до 31.10.2026).
  const handleStatusChange = async (row: Row, status: 'sold' | 'canceled' | 'return') => {
    if (!row.rowIds.length) {
      message.error('Не найдены строки заказа — обновите страницу');
      return;
    }
    const key = row.notifId;
    // Instant UI update
    setOverrideStatus(prev => ({ ...prev, [key]: BACKEND_TO_NOTIF[status] ?? status }));
    try {
      await updateOrderStatus(row.rowIds, status);
      await onStatusChanged?.();
    } catch (e) {
      // Revert on error
      setOverrideStatus(prev => { const n = { ...prev }; delete n[key]; return n; });
      console.error(e);
    }
  };

  // Скидка — на один заказ (его строки), а не на телефон: до 31.10.2026 она ложилась
  // на все заказы покупателя.
  const handleDiscountChange = async (row: Row, discount: number | null) => {
    if (discount === null) return;
    if (!row.rowIds.length) {
      message.error('Не найдены строки заказа — обновите страницу');
      return;
    }
    const key = row.notifId;
    const pct = Math.min(100, Math.max(0, discount));
    // Optimistic update — показываем новое значение сразу
    setDiscountOverrides(prev => ({ ...prev, [key]: pct }));
    setSavingDiscount(prev => new Set([...prev, key]));
    try {
      await setOrderDiscount(row.rowIds, pct);
      message.success('Скидка сохранена');
      await onStatusChanged?.();
    } catch (e) {
      // Откат при ошибке
      setDiscountOverrides(prev => { const n = { ...prev }; delete n[key]; return n; });
      message.error('Ошибка сохранения скидки');
      console.error(e);
    } finally {
      setSavingDiscount(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const o of orders) {
      const ts = new Date((o as any).createdAt).getTime();
      const date = formatDateTime(ts);
      const status = (o as any).status ?? '—';
      const items = Array.isArray((o as any).items) ? (o as any).items : [];
      const notifId = String((o as any).id ?? (o as any).orderNumber ?? '');
      const rowIds: number[] = Array.isArray((o as any).rowIds) ? (o as any).rowIds : [];
      const phone = (o as any).phone;

      const cats: string[] | undefined = (o as any).categories;
      const types: string[] | undefined = (o as any).productTypes;
      const orderSkus: string[] | undefined = (o as any).skus;
      const productLabel = cats?.length
        ? cats.map(c => CATEGORY_LABELS[c] ?? c).join(', ')
        : (o as any).itemType ?? '—';
      const qualityLabel = types?.length
        ? types.map(t => PRODUCT_TYPE_LABELS[t] ?? t).join(', ')
        : (o as any).quality ?? '—';
      const skuLabel = orderSkus?.length ? orderSkus.join('\n') : '—';

      // orderTotal — сумма всех товаров в заказе (order-level)
      const orderTotal = Number((o as any).orderTotal ?? (o as any).total ?? 0);
      // Скидка: optimistic override → данные из notification → 0
      const discount = discountOverrides[notifId] ?? Number((o as any).discount ?? 0);
      // Итого считается всегда локально
      const effectiveTotal = orderTotal * (1 - discount / 100);

      if (items.length === 0) {
        out.push({
          key: `${notifId}-0`,
          notifId, rowIds, phone,
          orderNumber: (o as any).orderNumber,
          date, dateTs: ts, status,
          product: productLabel, quality: qualityLabel,
          sku: skuLabel, qty: 0, price: null,
          total: orderTotal,       // Сумма = order total
          discount, effectiveTotal,
        });
      } else {
        items.forEach((it: any, idx: number) => {
          out.push({
            key: `${notifId}-${idx}`,
            notifId, rowIds, phone,
            orderNumber: (o as any).orderNumber,
            date, dateTs: ts, status,
            product: productLabel, quality: qualityLabel,
            sku: skuLabel,
            qty: Number(it.quantity ?? it.qty ?? 0),
            price: it.price ?? null,  // Цена = за единицу
            total: orderTotal,        // Сумма = order total (не item total)
            discount, effectiveTotal,
          });
        });
      }
    }
    return out.slice(0, maxRows);
  }, [orders, maxRows, discountOverrides]);

const baseColumns: ResCol<Row>[] = [
  {
    key: 'orderNumber',
    title: 'Номер заказа',
    dataIndex: 'orderNumber',
    width: 180,
    minWidth: 130,
    ellipsis: true,
    render: (value: string) => <span title={value}>{fmtOrderNo(value)}</span>,
    sorter: (a, b) => {
      const ax = orderNoWeight(a.orderNumber), bx = orderNoWeight(b.orderNumber);
      if (!Number.isNaN(ax) && !Number.isNaN(bx)) return ax - bx;
      return a.orderNumber.localeCompare(b.orderNumber, 'ru');
    },
  },
  {
    key: 'date',
    title: 'Дата',
    dataIndex: 'date',
    width: 170,
    minWidth: 150,
    align: 'left',
    sorter: (a, b) => a.dateTs - b.dateTs,
  },
  {
    key: 'sku',
    title: 'SKU',
    dataIndex: 'sku',
    width: 160,
    minWidth: 120,
    align: 'left',
    render: (v: string) => <span style={{ whiteSpace: 'pre-line' }}>{v}</span>,
    sorter: (a, b) => a.sku.localeCompare(b.sku, 'ru'),
  },
  {
    key: 'status',
    title: 'Статус',
    dataIndex: 'status',
    width: 150,
    minWidth: 130,
    align: 'left',
    sorter: (a, b) => a.status.localeCompare(b.status, 'ru'),
    render: (status: string) => {
      const map: Record<string, { color: string; label: string }> = {
        new:      { color: 'blue',   label: 'Новый' },
        shipped:  { color: 'cyan',   label: 'Продано' },
        canceled: { color: 'red',    label: 'Отменён' },
        returned: { color: 'purple', label: 'Возврат' },
      };
      const m = map[status] ?? { color: 'default', label: status };
      return <Tag color={m.color}>{m.label}</Tag>;
    },
  },
  {
    key: 'product',
    title: 'Товар',
    dataIndex: 'product',
    width: 160,
    minWidth: 140,
    align: 'left',
    sorter: (a, b) => a.product.localeCompare(b.product, 'ru'),
  },
  {
    key: 'quality',
    title: 'Тип товара',
    dataIndex: 'quality',
    width: 140,
    minWidth: 120,
    align: 'left',
    sorter: (a, b) => a.quality.localeCompare(b.quality, 'ru'),
  },
  {
    key: 'qty',
    title: 'Кол-во',
    dataIndex: 'qty',
    width: 100,
    minWidth: 90,
    align: 'left',
    sorter: (a, b) => a.qty - b.qty,
  },
  {
    key: 'price',
    title: 'Цена',
    dataIndex: 'price',
    width: 120,
    minWidth: 110,
    align: 'left',
    render: (v) => (v == null ? '—' : formatCurrency(v, 'AMD')),
    sorter: (a, b) => (a.price ?? -Infinity) - (b.price ?? -Infinity),
  },
  {
    key: 'total',
    title: 'Сумма',
    dataIndex: 'total',
    width: 130,
    minWidth: 110,
    align: 'left',
    render: (v) => (v == null ? '—' : <b>{formatCurrency(v, 'AMD')}</b>),
    sorter: (a, b) => (a.total ?? -Infinity) - (b.total ?? -Infinity),
  },
  {
    key: 'discount',
    title: 'Скидка %',
    dataIndex: 'discount',
    width: 120,
    minWidth: 100,
    align: 'center',
    render: (discount: number | undefined, row: Row) => {
      const saving = savingDiscount.has(row.notifId);
      const draft = discountDrafts[row.notifId];
      const commit = () => {
        if (draft === undefined) return;
        setDiscountDrafts(prev => { const n = { ...prev }; delete n[row.notifId]; return n; });
        if (draft !== null && draft !== (discount ?? 0)) void handleDiscountChange(row, draft);
      };
      return (
        <InputNumber
          min={0} max={100} step={1}
          value={draft !== undefined ? draft : (discount ?? 0)}
          onChange={(val) => setDiscountDrafts(prev => ({ ...prev, [row.notifId]: val }))}
          onBlur={commit}
          onPressEnter={commit}
          disabled={saving}
          style={{ width: 80 }}
          size="small"
        />
      );
    },
  },
  {
    key: 'effectiveTotal',
    title: 'Итого',
    dataIndex: 'effectiveTotal',
    width: 130,
    minWidth: 110,
    align: 'left',
    render: (v) => (v == null ? '—' : <b style={{ color: '#1890ff' }}>{formatCurrency(v, 'AMD')}</b>),
    sorter: (a, b) => (a.effectiveTotal ?? -Infinity) - (b.effectiveTotal ?? -Infinity),
  },
];

  const { columns, components } = useResizableColumns<Row>(baseColumns, {
    storageKey: 'table.ordersItems.cols.v3',
    enableVisibility: true,
    enableReorder: true,
  });

  const STATUS_LABELS: Record<string, string> = {
    new:      'Изменить',
    shipped:  'Продано',
    canceled: 'Отменён',
    returned: 'Возврат',
  };

  const actionColumn: ColumnsType<Row>[number] = {
    key: 'actions',
    title: 'Выбор статуса',
    width: 140,
    fixed: 'right',
    render: (_: any, row: Row) => {
      const effectiveStatus = overrideStatus[row.notifId] || row.status;
      const label = STATUS_LABELS[effectiveStatus] ?? 'Изменить';
      return (
        <Dropdown
          menu={{
            items: [
              {
                key: 'sold',
                label: 'Продано',
                onClick: () => handleStatusChange(row, 'sold'),
              },
              {
                key: 'canceled',
                label: 'Отменить',
                danger: true,
                onClick: () => handleStatusChange(row, 'canceled'),
              },
              {
                key: 'return',
                label: 'Возврат',
                onClick: () => handleStatusChange(row, 'return'),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button size="small" style={{ width: 110, textAlign: 'left' }}>{label} ▾</Button>
        </Dropdown>
      );
    },
  };

  const locale = { ...ruRU, Pagination: { ...(ruRU as any).Pagination, items_per_page: '' } } as any;

  const allColumns = [...(columns as ColumnsType<Row>), actionColumn];

  return (
    <ConfigProvider locale={locale}>
      <Table<Row>
        size="small"
        rowKey="key"
        dataSource={rows}
        columns={allColumns}
        components={components}
        scroll={{ x: 'max-content' }}
        pagination={{
          current, pageSize, position: ['bottomRight'], showSizeChanger: true,
          pageSizeOptions: ['20','50','100'],
          onChange: (p, s) => { setCurrent(p); setPageSize(s); },
          showLessItems: true,
        }}
      />
    </ConfigProvider>
  );
};

export default OrdersItemsTable;
